/* VaultThemeProvider — wraps the entire app. Lets any page/component
 * call `useVaultTheme()` to look up the catalog and (optionally) set
 * the *currently active theme*. Active theme = whichever user's space
 * we're currently viewing (your own profile, someone else's profile,
 * a thread you opened, a DM thread, etc.).
 *
 * The provider only owns *state*. The visual application of the theme
 * (background + floating player + marquee) is done by `ApplyTheme`
 * components that pages drop in their JSX.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const ThemeCtx = createContext(null);

const _THEMES_STORAGE_KEY = "vault.themesCatalog.v1";

function _hydrateThemesFromStorage() {
  try {
    const raw = sessionStorage.getItem(_THEMES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && (parsed.backgrounds?.length || parsed.sounds?.length)) return parsed;
  } catch { /* ignore */ }
  return null;
}
function _persistThemesToStorage(catalog) {
  try {
    sessionStorage.setItem(_THEMES_STORAGE_KEY, JSON.stringify(catalog));
  } catch { /* quota or private-mode — fine */ }
}

export function VaultThemeProvider({ children }) {
  const _seed = _hydrateThemesFromStorage();
  const [catalog, setCatalog] = useState(_seed || { backgrounds: [], sounds: [] });
  // If we have a seed, render immediately without a spinner. The
  // network call still runs to refresh in the background but the user
  // sees populated grids instantly.
  const [catalogLoading, setCatalogLoading] = useState(_seed == null);
  const [active, setActive] = useState(null);  // { theme, owner_label, source }
  const [myTheme, setMyTheme] = useState(null); // signed-in user's saved theme

  // Load the catalog with auto-retry. The full catalog is ~75KB and on
  // a slow connection can take 5+ seconds — a single transient blip used
  // to leave the entire app with empty sounds/backgrounds. We now keep
  // retrying with exponential backoff until we get a non-empty payload.
  useEffect(() => {
    let alive = true;
    let attempt = 0;
    const load = async () => {
      while (alive && attempt < 8) {
        try {
          const r = await api.get("/themes/catalog", { timeout: 45000 });
          const data = r.data || {};
          const bgs = Array.isArray(data.backgrounds) ? data.backgrounds : [];
          const snds = Array.isArray(data.sounds) ? data.sounds : [];
          if (bgs.length === 0 && snds.length === 0) {
            throw new Error("empty themes catalog");
          }
          if (alive) {
            const fresh = { backgrounds: bgs, sounds: snds };
            setCatalog(fresh);
            setCatalogLoading(false);
            _persistThemesToStorage(fresh);
          }
          return;
        } catch {
          attempt += 1;
          const delay = Math.min(1000 * 2 ** (attempt - 1), 30000);
          await new Promise((res) => setTimeout(res, delay));
        }
      }
      if (alive) setCatalogLoading(false);
    };
    load();
    api.get("/themes/me").then((r) => {
      if (alive) setMyTheme(r.data?.theme || null);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const saveMyTheme = useCallback(async (payload) => {
    const r = await api.put("/themes/me", payload);
    setMyTheme(r.data?.theme || null);
    return r.data?.theme || null;
  }, []);

  const clearMyTheme = useCallback(async () => {
    await api.put("/themes/me", { clear: true });
    setMyTheme(null);
  }, []);

  const setActiveTheme = useCallback((next) => {
    // next: null | { theme, ownerLabel, source }
    setActive(next || null);
  }, []);

  const value = {
    catalog,
    catalogLoading,
    active,
    myTheme,
    setActiveTheme,
    saveMyTheme,
    clearMyTheme,
    refreshMyTheme: async () => {
      try {
        const r = await api.get("/themes/me");
        setMyTheme(r.data?.theme || null);
      } catch (e) { /* ignore */ }
    },
  };
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useVaultTheme() {
  const v = useContext(ThemeCtx);
  if (!v) throw new Error("useVaultTheme must be used inside VaultThemeProvider");
  return v;
}
